import React, { useCallback, useRef, useState } from "react";
import i18next from "i18next";
import classNames from "classnames";
import { Feature } from "ol";
import { useMapContext } from "../../Contexts/MapContext";
import { getMap } from "../GeoLiteMap";
import { executeQueryAndReturnFieldInfo } from "../rest";
import { decodeFeatureFields } from "../utils";
import { IEsriFieldInfo } from "../interfaces";
import { useMapDownloadContext } from "../../Contexts/MapDownloadContext";

const maxResults = 100;
const searchField = "GRIDNUMBER";

export const SheetSearch = (): JSX.Element => {
    const { configA } = useMapContext();
    const { setIdentifiedSheet, sheetFeatures } = useMapDownloadContext();

    const [text, setText] = useState<string>("");
    const [results, setResults] = useState<Feature[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [expanded, setExpanded] = useState<boolean>(false);

    const fieldInfoRef = useRef<IEsriFieldInfo[]>();

    const executeSearch = useCallback(async () => {
        if (!text) {
            return;
        }
        setLoading(true);
        const query = buildSimpleQuery(text);
        const queryResults = await executeQueryAndReturnFieldInfo(query.url, query.params);
        !fieldInfoRef.current && (fieldInfoRef.current = queryResults.fieldInfo);
        setResults(queryResults.features.slice(0, maxResults));
        setLoading(false);
        queryResults.features.length && setExpanded(true);
    }, [text]);

    const onChange = (e) => {
        setResults([]);
        setText(e.target.value);
    };

    const onSelect = (selectedFeature: Feature) => {
        const decodedFeature = decodeFeatureFields(selectedFeature, fieldInfoRef.current);
        setIdentifiedSheet([decodedFeature]);
    };

    const toggleResults = useCallback(() => {
        setExpanded(!expanded);
    }, [expanded]);

    const onKeyDown = (e) => {
        e.key === "Enter" && executeSearch();
    };

    const buildSimpleQuery = useCallback(
        (searchText: string) => {
            const mapLayer = getMap()
                .getLayers()
                .getArray()
                .find((l) => l.get("id") === configA.geotelpa.searchLayerId);

            let layerQuery = `${searchField} LIKE '%{0}%'`;
            sheetFeatures.length > 0 &&
                (layerQuery +=
                    " AND NOT OBJECTID IN (" + sheetFeatures.map((f) => f.get("OBJECTID").toString()).join(",") + ")");

            const where = layerQuery.replace(new RegExp("\\{0\\}", "g"), searchText.toUpperCase());

            return {
                url: mapLayer.get("url") + "/0",
                params: {
                    f: "json",
                    where,
                    outFields: "*"
                }
            };
        },
        [sheetFeatures]
    );

    return (
        <div className="list-select">
            <label>{i18next.t("mapDownload.chooseFromList")} </label>
            <div className="search-bar">
                <div className="search-input">
                    <input type="text" placeholder="Meklēt " value={text} onChange={onChange} onKeyDown={onKeyDown} />
                    {loading && (
                        <div className="search-clear">
                            <span className="spinner-small"></span>
                        </div>
                    )}
                    {!loading && text && (
                        <div className="search-clear" onClick={toggleResults}>
                            <span className={expanded ? "icon-geolite-chevron-down" : "icon-geolite-chevron-up"}></span>
                        </div>
                    )}
                    <div className={classNames("search-list", { none: !expanded })}>
                        {results.map((result, i) => (
                            <div className="list-option" key={"option-" + i} onClick={() => onSelect(result)}>
                                {result.get(searchField)}
                            </div>
                        ))}
                    </div>
                </div>
                <div className="search-button" onClick={executeSearch} title={i18next.t("searchPanel.placeHolder")}>
                    <span className="icon-geolite-search"></span>
                </div>
            </div>
        </div>
    );
};
